var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSquare } from "@fortawesome/free-regular-svg-icons";
import { faCheckSquare, faChevronCircleRight, faEye, faEyeSlash, faMinus, faPlus, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, nextTick, onBeforeUnmount, ref, toRefs, watch } from "vue";
import { useWorkflowStores } from "@/composables/workflowStores";
import { assertDefined, ensureDefined } from "@/utils/assertions";
import { useRelativePosition } from "./composables/relativePosition";
import { useTerminal } from "./composables/useTerminal";
import { NULL_COLLECTION_TYPE_DESCRIPTION } from "./modules/collectionTypeDescription";
import DraggableWrapper from "./DraggablePan.vue";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
import ConnectionMenu from "@/components/Workflow/Editor/ConnectionMenu.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'NodeOutput',
    props: {
        output: null,
        workflowOutputs: null,
        stepType: null,
        stepId: null,
        postJobActions: null,
        stepPosition: null,
        rootOffset: null,
        scroll: null,
        scale: null,
        datatypesMapper: null,
        parentNode: null,
        readonly: { type: Boolean }
    },
    emits: ["pan-by", "stopDragging", "onDragConnector"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faSquare, faCheckSquare, faChevronCircleRight, faEye, faEyeSlash, faMinus, faPlus);
        const { stateStore, stepStore } = useWorkflowStores();
        const { rootOffset, output, stepId, datatypesMapper } = toRefs(props);
        const terminalComponent = ref(null);
        const terminalElement = computed(() => { var _a, _b; return (_b = (_a = terminalComponent.value) === null || _a === void 0 ? void 0 : _a.$el) !== null && _b !== void 0 ? _b : null; });
        const position = useRelativePosition(terminalElement, computed(() => props.parentNode));
        const extensions = computed(() => {
            let changeDatatype;
            if ("label" in props.output && props.postJobActions[`ChangeDatatypeAction${props.output.label}`]) {
                changeDatatype = props.postJobActions[`ChangeDatatypeAction${props.output.label}`];
            }
            else {
                changeDatatype = props.postJobActions[`ChangeDatatypeAction${props.output.name}`];
            }
            let extensions = (changeDatatype === null || changeDatatype === void 0 ? void 0 : changeDatatype.action_arguments.newtype) ||
                ("extensions" in props.output && props.output.extensions) ||
                ("type" in props.output && props.output.type) ||
                "unspecified";
            if (!Array.isArray(extensions)) {
                extensions = [extensions];
            }
            return extensions;
        });
        const effectiveOutput = ref(Object.assign(Object.assign({}, output.value), { extensions: extensions.value }));
        watch(extensions, () => {
            effectiveOutput.value = Object.assign(Object.assign({}, output.value), { extensions: extensions.value });
        });
        const { terminal, isMappedOver: isMultiple } = useTerminal(stepId, effectiveOutput, datatypesMapper);
        const workflowOutput = computed(() => props.workflowOutputs.find((workflowOutput) => workflowOutput.output_name == props.output.name));
        const isVisible = computed(() => {
            const isHidden = `HideDatasetAction${props.output.name}` in props.postJobActions;
            return !isHidden;
        });
        const visibleHint = computed(() => {
            if (isVisible.value) {
                return `Output will be visible in history. Click to hide output.`;
            }
            else {
                return `Output will be hidden in history. Click to make output visible.`;
            }
        });
        const label = computed(() => {
            var _a;
            return ((_a = workflowOutput.value) === null || _a === void 0 ? void 0 : _a.label) || props.output.name;
        });
        const rowClass = computed(() => {
            var _a;
            const classes = ["form-row", "dataRow", "output-data-row"];
            if ("valid" in props.output && ((_a = props.output) === null || _a === void 0 ? void 0 : _a.valid) === false) {
                classes.push("form-row-error");
            }
            return classes;
        });
        const menu = ref();
        const showChildComponent = ref(false);
        function closeMenu() {
            showChildComponent.value = false;
        }
        function toggleChildComponent() {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                showChildComponent.value = !showChildComponent.value;
                if (showChildComponent.value) {
                    yield nextTick();
                    if (((_a = menu.value) === null || _a === void 0 ? void 0 : _a.$el) instanceof HTMLElement) {
                        menu.value.$el.focus();
                    }
                }
                else {
                    terminalElement.value.focus();
                }
            });
        }
        function onToggleActive() {
            if (props.readonly) {
                return;
            }
            const step = stepStore.getStep(stepId.value);
            assertDefined(step);
            let stepWorkflowOutputs = [...(step.workflow_outputs || [])];
            if (workflowOutput.value) {
                stepWorkflowOutputs = stepWorkflowOutputs.filter((workflowOutput) => workflowOutput.output_name !== output.value.name);
            }
            else {
                stepWorkflowOutputs.push({ output_name: output.value.name, label: output.value.name });
            }
            stepStore.updateStep(Object.assign(Object.assign({}, step), { workflow_outputs: stepWorkflowOutputs }));
        }
        function onToggleVisible() {
            if (props.readonly) {
                return;
            }
            const actionKey = `HideDatasetAction${props.output.name}`;
            const step = Object.assign({}, ensureDefined(stepStore.getStep(stepId.value)));
            if (isVisible.value) {
                step.post_job_actions = Object.assign(Object.assign({}, step.post_job_actions), { [actionKey]: {
                        action_type: "HideDatasetAction",
                        output_name: props.output.name,
                        action_arguments: {},
                    } });
            }
            else {
                if (step.post_job_actions) {
                    const _a = step.post_job_actions, _b = actionKey, _unused = _a[_b], newPostJobActions = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                    step.post_job_actions = newPostJobActions;
                }
                else {
                    step.post_job_actions = {};
                }
            }
            stepStore.updateStep(step);
        }
        function onPanBy(panBy) {
            emit("pan-by", panBy);
        }
        function onStopDragging() {
            isDragging.value = false;
            dragX.value = 0;
            dragY.value = 0;
            emit("stopDragging");
        }
        const dragX = ref(0);
        const dragY = ref(0);
        const isDragging = ref(false);
        const startX = computed(() => { var _a, _b, _c, _d; return position.value.offsetLeft + ((_b = (_a = props.stepPosition) === null || _a === void 0 ? void 0 : _a.left) !== null && _b !== void 0 ? _b : 0) + ((_d = (_c = terminalElement.value) === null || _c === void 0 ? void 0 : _c.offsetWidth) !== null && _d !== void 0 ? _d : 2) / 2; });
        const startY = computed(() => { var _a, _b, _c, _d; return position.value.offsetTop + ((_b = (_a = props.stepPosition) === null || _a === void 0 ? void 0 : _a.top) !== null && _b !== void 0 ? _b : 0) + ((_d = (_c = terminalElement.value) === null || _c === void 0 ? void 0 : _c.offsetHeight) !== null && _d !== void 0 ? _d : 2) / 2; });
        const endX = computed(() => {
            return (dragX.value || startX.value) + props.scroll.x.value / props.scale;
        });
        const endY = computed(() => {
            return (dragY.value || startY.value) + props.scroll.y.value / props.scale;
        });
        const dragPosition = computed(() => {
            return {
                startX: startX.value,
                endX: endX.value,
                startY: startY.value,
                endY: endY.value,
            };
        });
        watch([dragPosition, isDragging], () => {
            if (isDragging.value) {
                emit("onDragConnector", dragPosition.value, terminal.value);
            }
        });
        watch([startX, startY], ([x, y]) => {
            stateStore.setOutputTerminalPosition(props.stepId, props.output.name, { startX: x, startY: y });
        }, {
            immediate: true,
        });
        function onMove(dragPosition) {
            dragX.value = dragPosition.x + terminalElement.value.clientWidth / 2;
            dragY.value = dragPosition.y + terminalElement.value.clientHeight / 2;
        }
        const id = computed(() => `node-${props.stepId}-output-${props.output.name}`);
        const showCalloutActiveOutput = computed(() => props.stepType === "tool" || props.stepType === "subworkflow");
        const showCalloutVisible = computed(() => props.stepType === "tool");
        function collectionTypeToDescription(collectionTypeDescription) {
            let collectionDescription = collectionTypeDescription.collectionType;
            if (collectionTypeDescription &&
                collectionTypeDescription.isCollection &&
                collectionTypeDescription.collectionType) {
                // we'll give a prettier label to the must common nested lists
                switch (collectionTypeDescription.collectionType) {
                    case "list:paired": {
                        collectionDescription = "list of pairs dataset collection";
                        break;
                    }
                    case "list:list": {
                        collectionDescription = "list of lists dataset collection";
                        break;
                    }
                    default: {
                        if (collectionTypeDescription.rank > 1) {
                            collectionDescription = `dataset collection with ${collectionTypeDescription.rank} levels of nesting`;
                        }
                        break;
                    }
                }
            }
            return collectionDescription;
        }
        const outputDetails = computed(() => {
            let collectionType = "collectionType" in terminal.value && terminal.value.collectionType;
            const outputType = collectionType && collectionType.isCollection && collectionType.collectionType
                ? `output is ${collectionTypeToDescription(collectionType)}`
                : `output is dataset`;
            if (isMultiple.value) {
                if (!collectionType) {
                    collectionType = NULL_COLLECTION_TYPE_DESCRIPTION;
                }
                const effectiveOutputType = terminal.value.mapOver.append(collectionType);
                return `${outputType} and mapped-over to produce a ${collectionTypeToDescription(effectiveOutputType)} `;
            }
            return outputType;
        });
        const isDuplicateLabel = computed(() => {
            const duplicateLabels = stepStore.duplicateLabels;
            return Boolean(label.value && duplicateLabels.has(label.value));
        });
        const labelClass = computed(() => {
            if (isDuplicateLabel.value) {
                return "alert-info";
            }
            return null;
        });
        const labelToolTipTitle = computed(() => {
            var _a;
            return `Output label '${(_a = workflowOutput.value) === null || _a === void 0 ? void 0 : _a.label}' is not unique`;
        });
        onBeforeUnmount(() => {
            stateStore.deleteOutputTerminalPosition(props.stepId, props.output.name);
        });
        const addTagsAction = computed(() => {
            var _a, _b, _c, _d;
            return (_d = (_c = (_b = (_a = props.postJobActions[`TagDatasetAction${props.output.name}`]) === null || _a === void 0 ? void 0 : _a.action_arguments) === null || _b === void 0 ? void 0 : _b.tags) === null || _c === void 0 ? void 0 : _c.split(",")) !== null && _d !== void 0 ? _d : [];
        });
        const removeTagsAction = computed(() => {
            var _a, _b, _c, _d;
            return (_d = (_c = (_b = (_a = props.postJobActions[`RemoveTagDatasetAction${props.output.name}`]) === null || _a === void 0 ? void 0 : _a.action_arguments) === null || _b === void 0 ? void 0 : _b.tags) === null || _c === void 0 ? void 0 : _c.split(",")) !== null && _d !== void 0 ? _d : [];
        });
        return { __sfc: true, props, emit, stateStore, stepStore, rootOffset, output, stepId, datatypesMapper, terminalComponent, terminalElement, position, extensions, effectiveOutput, terminal, isMultiple, workflowOutput, isVisible, visibleHint, label, rowClass, menu, showChildComponent, closeMenu, toggleChildComponent, onToggleActive, onToggleVisible, onPanBy, onStopDragging, dragX, dragY, isDragging, startX, startY, endX, endY, dragPosition, onMove, id, showCalloutActiveOutput, showCalloutVisible, collectionTypeToDescription, outputDetails, isDuplicateLabel, labelClass, labelToolTipTitle, addTagsAction, removeTagsAction, FontAwesomeIcon, DraggableWrapper, StatelessTags, ConnectionMenu };
    }
});
